import React from 'react';
import { Collapse, Descriptions, Drawer } from 'antd';
import { useUnit } from 'effector-react';
import {
  $chartData,
  $projectModal,
  $regionsDict,
  resetProjectModalEv,
} from '../../models/investProModel/index.js';
import { popup_props_transaltion } from '../../dictionaries/popup_props_translation.js';
import { addSeparators } from '../../utils/format-utils.js';
import PopulationLocationChart from '../Charts/PopulationLocationChart.jsx';
import PopulationAgeChart from '../Charts/PopulationAgeChart.jsx';
import IncomeChart from '../Charts/IncomeChart.jsx';
import VRPChart from '../Charts/VRPChart.jsx';
import EmploymentChart from '../Charts/EmploymentChart.jsx';
import UnemploymentChart from '../Charts/UnemploymentChart.jsx';
import EmploymentActivityChart from '../Charts/EmploymentActivityChart.jsx';
import EmployedPopulation from '../Charts/EmployedPopulation.jsx';
import BusinessChart from '../Charts/BusinessChart.jsx';
import useWindowSize from '../../hooks/useWindowSize.js';
import TradeTurnover from '../Charts/TradeTurnover.jsx';
import RoadsInRegions from '../Charts/RoadsInRegions.jsx';

const excluded_keys = [
  'source',
  'feature_id',
  'id',
  'gen_id',
  'detail_page_url',
  'osm_id',
  'lvl',
  'index_main',
  'region',
  'code',
  'subregion',
  'ident',
  'zone_id',
  'zoom',
  'type',
  'center',
  'name_en',
  'name_kz',
  'date_create',
  'short_desc_en',
  'short_desc_kz',
  'district',
  'trade_turnover',
];

const invest_point_priority = [
  'name_ru',
  'lvl_1_code',
  'lvl_2_code',
  'gen_name',
  'short_desc_ru',
  'investment_sum',
  'nvp_sum',
  'irr',
  'ebitda',
  'payback_period',
];

const propsToShowDict = {
  invest_pro_districts_source: [
    'lvl_1_code',
    'lvl_2_code',
    'total_population',
    'count_household',
    'income',
    // 'vrp_mln_tg',
    'vrp',
    'unemployment_rate',
    'inflation',
    'trade_turnover',
  ],
  invest_pro_regions_source: [
    'lvl_1_code',
    'total_population',
    'count_household',
    'income',
    // 'vrp_mln_tg',
    'vrp',
    'unemployment_rate',
    'inflation',
  ],
  invest_pro_iz_dots_source: [
    'name',
    'lvl_1_code',
    'lvl_2_code',
    'description',
  ],
  invest_pro_sez_dots_source: [
    'name',
    'lvl_1_code',
    'lvl_2_code',
    'description',
  ],
  invest_pro_kz_helicopter_source: [
    'name',
    'lvl_1_code',
    'lvl_2_code',
    'municipality',
    'iata_code',
    'icao_code',
  ],
  invest_pro_kz_airports_source: [
    'name',
    'lvl_1_code',
    'lvl_2_code',
    'municipality',
    'iata_code',
    'icao_code',
  ],
};

function ProjectModal() {
  const projectModal = useUnit($projectModal);
  const resetProjectModal = useUnit(resetProjectModalEv);
  const regionsDict = useUnit($regionsDict);
  const chartData = useUnit($chartData);
  const [width, height] = useWindowSize();

  if (!projectModal) return null;

  const formatData = () => {
    const result = [];
    let propsToShow =
      propsToShowDict[projectModal.source] ||
      Object.keys(projectModal.properties);
    propsToShow = propsToShow.filter((item) => {
      return projectModal.properties[item];
    });
    propsToShow.forEach((item) => {
      if (!excluded_keys.includes(item)) {
        let children = projectModal.properties[item];
        if (item === 'lvl_1_code' || item === 'lvl_2_code') {
          children = regionsDict[children].name;
        }

        if (!Number.isNaN(Number(children))) {
          children = addSeparators(Number(children));
        }

        if (
          item === 'description' &&
          (projectModal.properties.source.includes('iz') ||
            projectModal.properties.source.includes('sez'))
        ) {
          const parsedDesc = JSON.parse(projectModal.properties[item]);
          Object.keys(parsedDesc).map((key) => {
            result.push({
              key: item,
              label: parsedDesc[key].label,
              children: parsedDesc[key].value,
            });
          });
        } else {
          result.push({
            key: item,
            label: popup_props_transaltion[item] || item,
            children,
          });
        }
      }
    });

    if (projectModal.properties.source === 'test_invest_points') {
      result.sort((a, b) => {
        return (
          invest_point_priority.indexOf(a.key) -
          invest_point_priority.indexOf(b.key)
        );
      });
    }

    return result;
  };

  const formatCollapseItems = () => {
    return [
      {
        key: '1',
        label: 'Паспорт региона',
        children: (
          <Descriptions
            items={formatData()}
            column={1}
            size="small"
            layout="vertical"
          />
        ),
      },
      {
        key: '2',
        label: 'Социальный профиль',
        children: (
          <div>
            <PopulationLocationChart data={projectModal.properties} />
            <PopulationAgeChart data={chartData?.populationByAgeData || []} />
            <IncomeChart data={chartData?.salaryByAgeData || []} />
          </div>
        ),
      },
      {
        key: '3',
        label: 'Экономический профиль',
        children: (
          <div>
            <TradeTurnover data={chartData?.tradeTurnoverData || {}} />
            <VRPChart data={chartData?.vrpData || []} />
            <EmploymentChart data={chartData?.employmentRateData || []} />
            <UnemploymentChart data={chartData?.unemploymentRateData || []} />
            <EmploymentActivityChart data={chartData?.laborPercentData || []} />
            <EmployedPopulation data={chartData?.laborCountData || []} />
            <BusinessChart data={chartData?.poiData || []} />
            <RoadsInRegions data={chartData?.roadsInRegionsData || []} />
          </div>
        ),
      },
    ];
  };

  return (
    <Drawer
      className="glass"
      width={width <= 425 ? '80vw' : '30vw'}
      open={!!projectModal}
      onClose={resetProjectModal}
      mask={false}
      styles={{
        content: {
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: 20,
        },
      }}
    >
      {projectModal.properties.source === 'regions_with_population' ? (
        <Collapse items={formatCollapseItems()} />
      ) : (
        <Descriptions
          items={formatData()}
          column={1}
          size="small"
          layout="vertical"
        />
      )}
    </Drawer>
  );
}

export default ProjectModal;
